import {
  Col,
  DataGrid,
  DatagridCheckboxHelpers,
  DataGridColumnType,
  Flex,
  Row,
  SortDirection,
} from '@appliedsystems/applied-design-system';
import { DataGridProps } from '@appliedsystems/applied-design-system/dist/components/DataGrid/DataGrid';
import { CurrencyCode, EpicInvoice, FinanceableInvoice, LocaleCode, toIntlFormat } from '@appliedsystems/payments-core';
import { groupBy, map, mapValues, minBy } from '@s-libs/micro-dash';
import React, { FC, useEffect, useMemo } from 'react';
import { usePaymentsTranslation } from '../../../hooks/usePaymentsTranslation';
import classes from './InvoiceDataGrid.module.scss';

export type InvoiceGroup = {
  invoiceNumber: string;
  dueDate: string;
  amountDue: string;
  amountDueMinorUnits: number;
  financingOffer?: string;
  firstDescription?: string;
  transactions: InvoiceDataGridRow[];
};

export type InvoiceDataGridRow = Pick<
  EpicInvoice,
  'invoiceNumber' | 'amountDue' | 'dueDate' | 'description' | 'transactionId' | 'itemNumber' | 'policyNumber'
>;

type Props = {
  invoices: InvoiceDataGridRow[] | null;
  onChange: (invoices: InvoiceGroup[]) => void;
  locale: LocaleCode;
  financeableInvoices?: FinanceableInvoice[];
  preSelectedInvoices?: string[];
  shouldDisableCheckbox?: (row: InvoiceGroup) => { disabled: boolean; toolTip: string };
};

const LocaleCurrencyMap: { [locale in LocaleCode]: CurrencyCode } = {
  'en-US': CurrencyCode.USD,
  'en-CA': CurrencyCode.CAD,
  'fr-CA': CurrencyCode.CAD,
  'en-GB': CurrencyCode.GBP,
};

export const InvoiceDataGrid = ({
  invoices,
  financeableInvoices,
  locale,
  onChange,
  preSelectedInvoices,
  shouldDisableCheckbox,
}: Props) => {
  const { t } = usePaymentsTranslation();
  const [rows, setRows] = React.useState<InvoiceGroup[]>();
  const checkboxes = React.useRef<DatagridCheckboxHelpers | null>(null);
  const [checkboxesInitialized, setCheckboxesInitialized] = React.useState(false);
  const currencyCode = LocaleCurrencyMap[locale];

  const makeFinancingOfferText = React.useCallback(
    (invoiceNumber: string, financeableInvoices: FinanceableInvoice[]) => {
      const matchingFinanceableInvoices = financeableInvoices.filter(
        (invoice: FinanceableInvoice) => invoice.invoiceNumber === invoiceNumber,
      );

      if (matchingFinanceableInvoices.length > 1) {
        console.error(`More than 1 financeable invoice matched with invoice number: ${invoiceNumber}`);
      }

      if (matchingFinanceableInvoices.length < 1) {
        return;
      }

      const [{ downPaymentAmount, numberOfInstallments, installmentFrequency, installmentAmount }] =
        matchingFinanceableInvoices;

      return t('PREMIUM_FINANCE_OFFER_TABLE_BODY', undefined, {
        downPaymentAmount: toIntlFormat({ amount: downPaymentAmount, currencyCode }, locale),
        installmentAmount: toIntlFormat({ amount: installmentAmount, currencyCode }, locale),
        installmentFrequency: installmentFrequency ? t(`INSTALLMENT_FREQUENCY_TYPE_${installmentFrequency}`) : '',
        numberOfInstallments,
      } as any);
    },
    [currencyCode, locale, t],
  );

  React.useEffect(() => {
    if (invoices) {
      const result = groupTransactionsIntoInvoices(invoices, currencyCode, locale).map((group) => ({
        ...group,
        ...(financeableInvoices
          ? { financingOffer: makeFinancingOfferText(group.invoiceNumber, financeableInvoices) }
          : {}),
      }));

      setRows(result);
    } else setRows(undefined);
  }, [currencyCode, financeableInvoices, makeFinancingOfferText, invoices, locale]);

  useEffect(() => {
    if (!rows || !checkboxesInitialized || !preSelectedInvoices) return;
    // When rows change, disregard any previous selections
    checkboxes.current?.checkNone();
    checkboxes.current?.toggleChecked(
      // This typescript cast is necessary because
      // rowIdField is a string, but toggleChecked
      // expects an array of numbers due to ADS type bug
      preSelectedInvoices as unknown as number[],
    );
  }, [rows, preSelectedInvoices, checkboxesInitialized]);

  const additionalColumns = useMemo((): DataGridColumnType<InvoiceGroup>[] => {
    // should only render a financing column if one or more of the outstanding invoices is financeable
    const shouldShowFinancingColumn = rows?.some((invoice) =>
      financeableInvoices?.some((financeableInvoice) => financeableInvoice.invoiceNumber === invoice.invoiceNumber),
    );

    if (shouldShowFinancingColumn) {
      return [{ name: 'financingOffer', title: t('PREMIUM_FINANCE_OFFER_TABLE_HEADER') }];
    }
    return [];
  }, [financeableInvoices, rows, t]);

  return (
    <Flex>
      <Row>
        <Col xs={12}>
          <InvoiceGroupTable
            rows={rows}
            locale={locale}
            additionalColumns={additionalColumns}
            checkbox={{
              enabled: true,
              helpers: (helpers: DatagridCheckboxHelpers) => {
                checkboxes.current = helpers;
                setCheckboxesInitialized(true);
              },
              renderer: (row) => {
                if (!row || !shouldDisableCheckbox) return { disabled: false };

                return shouldDisableCheckbox(row);
              },
            }}
            onCheckedRowsChange={(_data, helpers) => {
              if (!rows) return;
              const checked = helpers.getChecked();
              const ids = checked.ids.map((e) => e.toString());
              const checkedInvoices = rows.filter((e) => ids.includes(e.invoiceNumber));
              onChange(checkedInvoices);
            }}
          />
        </Col>
      </Row>
    </Flex>
  );
};

export const InvoiceGroupTable: FC<
  {
    rows: InvoiceGroup[] | undefined;
    locale: LocaleCode;
    additionalColumns?: DataGridColumnType<InvoiceGroup>[];
  } & Partial<Exclude<DataGridProps, 'rows'>>
> = ({ rows, locale, additionalColumns, ...dgProps }) => {
  const { t } = usePaymentsTranslation();
  const currencyCode = LocaleCurrencyMap[locale];

  const columns = React.useMemo((): DataGridColumnType<InvoiceGroup>[] => {
    const dataGridColumns = [
      {
        name: 'invoiceNumber',
        title: t('INVOICE_NUMBER'),
        sorted: SortDirection.Asc,
      },
      { name: 'dueDate', title: t('DUE_DATE') },
      { name: 'amountDue', title: t('AMOUNT_DUE') },
      ...(additionalColumns ?? []),
    ];

    return dataGridColumns;
  }, [additionalColumns, t]);

  return (
    <DataGrid<InvoiceGroup>
      testId="invoiceDataGridTest"
      rowIdField="invoiceNumber"
      columns={columns}
      rows={rows ?? [null]}
      rowChildContainer={(row) => {
        const childRows = row.transactions;
        return (
          <div>
            <table className={classes.flexTable}>
              <thead>
                <tr>
                  <th>{t('ITEM_NUMBER')}</th>
                  <th>{t('DESCRIPTION')}</th>
                  <th>{t('POLICY_NUMBER')}</th>
                  <th>{t('DUE_DATE')}</th>
                  <th>{t('AMOUNT_DUE')}</th>
                </tr>
              </thead>
              <tbody>
                {childRows.map((e) => {
                  return (
                    <tr key={`row-${e.transactionId}`}>
                      <td data-label={t('ITEM_NUMBER')}>{e.itemNumber}</td>
                      <td data-label={t('DESCRIPTION')}>{e.description}</td>
                      <td data-label={t('POLICY_NUMBER')}>{e.policyNumber}</td>
                      <td data-label={t('DUE_DATE')}>{new Date(e.dueDate).toLocaleDateString(locale)}</td>

                      <td data-label={t('AMOUNT_DUE')}>
                        {toIntlFormat(
                          {
                            amount: +e.amountDue.units * 100 + e.amountDue.partialUnits,
                            currencyCode,
                          },
                          locale,
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }}
      searchable={{
        enabled: (rows?.length ?? 0) > 1,
      }}
      {...dgProps}
    />
  );
};

export const groupTransactionsIntoInvoices = (
  invoices: InvoiceDataGridRow[],
  currencyCode: CurrencyCode,
  locale: LocaleCode,
): InvoiceGroup[] => {
  const groupedInvoices = groupBy(invoices, (e) => e.invoiceNumber);
  const aggregateValues = mapValues(groupedInvoices, (group) => ({
    amountDue: group.reduce(
      (sum, invoice) => sum + (+invoice.amountDue.units * 100 + invoice.amountDue.partialUnits),
      0,
    ),
    dueDate: minBy(group, (invoice) => new Date(invoice.dueDate).getTime())?.dueDate,
    firstDescription: group[0].description,
    transactions: group,
  }));
  const result = map(aggregateValues, (value, key) => {
    const invoiceNumber = key;
    return {
      invoiceNumber,
      amountDue: toIntlFormat({ amount: value.amountDue, currencyCode }, locale),
      amountDueMinorUnits: value.amountDue,
      dueDate: new Date(value.dueDate).toLocaleDateString(locale),
      firstDescription: value.firstDescription,
      transactions: value.transactions,
    };
  });
  return result;
};
